import { Node } from "@xyflow/react";
import { type RemirrorJSON } from "@remirror/core-types";
import { NOT_PERSISTED_NODE_TYPES } from "@zapier/canvas-constants";
import {
  DefaultNodeData,
  EphemeralNodeType,
  NodeData,
  NodeDataWithLabel,
  NodeType,
  TextNodeData,
  ZapNodeData,
  OutgoingBackendNode,
  NodeDataWithAttribution,
  NodeDataWithNotes,
  NodeAttributionSourceType,
  NodeDataWithActors,
  NodeDataWithColor,
  Time,
} from "../types";

/* convertCanvasSchemaForReactFlow function will hydrate props that we need on page load */
export function processNodesBeforeSave(nodes: Node[]): OutgoingBackendNode[] {
  return nodes.reduce((acc: OutgoingBackendNode[], node: Node) => {
    /** Some nodes are ephemeral and should not be stored */
    if (
      node.type &&
      NOT_PERSISTED_NODE_TYPES.includes(node.type as EphemeralNodeType)
    )
      return acc;

    /** Remove the 'selected' property from each node so when you open a Canvas, nothing is selected.
     * And we also don't need to persist the 'hidden' or 'className' props since UI presentation should not be stored as state.
     * */

    const {
      className,
      selected,
      hidden,
      dragging,
      selectable,
      deletable,
      connectable,
      extent, // For children of group nodes
      expandParent, // For children of group nodes
      style, // For text nodes
      ...rest
    } = node;

    const backendNode = convertReactFlowNodeToBackendNode(rest);
    acc.push(backendNode);

    return acc;
  }, []);
}

const stringifyLabel = (label?: string | RemirrorJSON): string => {
  // Text node label can be undefined on initial creation
  if (!label) {
    return "";
  }

  if (typeof label === "string") {
    return label;
  }

  if (typeof label === "object") {
    return JSON.stringify(label);
  }

  return String(label);
};

/**
 * Converts a ReactFlow Node with appropriate type data to OutgoingBackendNode type.
 * Ensures all necessary fields are correctly mapped.
 * Reverse of convertBackendNodeToReactFlowNode
 */
export function convertReactFlowNodeToBackendNode(
  node: Node<NodeData>,
): OutgoingBackendNode {
  const baseNode = {
    id: node.id,
    node_type: node.type as NodeType,
    x: node.position.x,
    y: node.position.y,
    label: stringifyLabel((node.data as NodeDataWithLabel).label),
    zap_id: (node.data as ZapNodeData).zapId ?? null,
    table_id: (node.data as DefaultNodeData).tableId ?? null,
    chatbot_id: (node.data as DefaultNodeData).chatbotId ?? null,
    project_id: (node.data as DefaultNodeData).projectId ?? null,
    page_id: (node.data as DefaultNodeData).pageId ?? null,
    is_expanded: (node.data as ZapNodeData).isExpanded ?? null,
    notes: (node.data as NodeDataWithNotes).notesJson ?? null,
    color: (node.data as NodeDataWithColor).color ?? null,
    background_color: (node.data as TextNodeData).backgroundColor ?? null,
    app: (node.data as DefaultNodeData).app ?? null,
    services: (node.data as DefaultNodeData).services ?? null,
    actors: (node.data as NodeDataWithActors).actors ?? null,
    width: node.measured?.width as number,
    height: node.measured?.height as number,
    parent_id: node.parentId ?? null,
    time_spent: (node.data as DefaultNodeData).timeSpent?.value
      ? ((node.data as DefaultNodeData).timeSpent as Time)
      : null,
  };

  const attributionSourceType = (node.data as NodeDataWithAttribution)
    .attributionSourceType;

  if (attributionSourceType) {
    return {
      ...baseNode,
      attribution_source_type:
        attributionSourceType as NodeAttributionSourceType,
    };
  }

  return baseNode;
}
